import { Dispatch, useCallback, useRef } from 'react'

import { deriveColumnOrderFromStateChange, ReportAction } from './useReportCoreState'

/**
 * Tracks live column order changes from OneDataCollection without
 * dispatching to state (which would cause a feedback loop).
 * Flushes the accumulated changes to state on demand (e.g. before save).
 */
export function useColumnOrderTracking(
  currentColumnOrder: string[] | null,
  dispatch: Dispatch<ReportAction>
) {
  const hasMountedRef = useRef(false)
  const liveColumnOrderRef = useRef<string[] | null>(null)

  const handleStateChange = useCallback(
    (
      settings: { visualization?: { table?: { order?: string[]; hidden?: string[] } } } | undefined
    ) => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true
        return
      }
      const nextColumnOrder = deriveColumnOrderFromStateChange(
        settings,
        liveColumnOrderRef.current ?? currentColumnOrder
      )
      if (nextColumnOrder !== undefined) {
        liveColumnOrderRef.current = nextColumnOrder
      }
    },
    [currentColumnOrder]
  )

  const flushColumnOrder = useCallback(() => {
    if (liveColumnOrderRef.current !== null) {
      dispatch({ type: 'COLUMN_ORDER_UPDATED', payload: liveColumnOrderRef.current })
    }
  }, [dispatch])

  return { handleColumnStateChange: handleStateChange, flushColumnOrder }
}
